// Port 3 (Bild-Masken/Schatten/Blur) — aus Jans Renderer uebernommen.
//
// HERKUNFT
//
// pro-editor/src/storyforger/Renderer/RendererWebGL.ts — zehn Methoden
// (syncImageMask, syncImageBlur, syncImageShadow, syncImageRealShadows,
// makeBlurredTexture, drawMaskInto, drawMaskShape, getCustomMaskSource,
// buildMaskSourceCanvas, injectShadowLift). Zusammen ~640 Zeilen.
//
// NICHT uebernommen: imageAlphaNeeded (allgemeine Bild-Opazitaet, die unser
// updateImage anders loest) und injectShadowLiftInto (nur updateObject/
// object3d — der gegatete Bereich, kommt mit dem Material-Merge).
//
// WARUM ALS EIGENES MODUL
//
// Gemessen vor dem Port: die Gruppe greift in ~640 Zeilen genau DREIMAL nach
// aussen — shadowLiftUniform, maskSourceCache, maskTextureLoading. Alles
// andere ist layerMesh-/Material-/userData-lokal. Sechs der zehn Funktionen
// sind REIN; die vier, die Zustand brauchen, bekommen ihn als `host`-Parameter.
// Das ist die EINZIGE Umformung gegenueber Jans Fassung (`this.` -> `host.`
// bzw. freie Funktion) — genau deshalb maschinell gegenpruefbar.
//
// GATE (gemessen 2026-07-22 gegen die dev-Datenbank, MERGE-GATE-BEFUND):
// Von 215 bestehenden Scrollys nutzt KEINER Masken, Schatten oder Blur. Der
// `case` im image-Zweig von renderLayer kann den Bestand also nicht
// veraendern — additiv-sicher.
//
// WAS DER PORT NICHT LEISTET
//
// Das Ergebnis ist WebGL/Canvas — ob eine Masken-Form korrekt sitzt oder ein
// Schatten richtig faellt, sagt kein Test. Die Pruefung ist visuell.
import THREE from "../utils/THREE";
import type { ImageContent } from "@scrolly/post-types";
import { blurCanvasFallback } from "./canvasBlur";
import { maskenRechteck } from "../utils/maskenRechteck";

/**
 * Der Teil des Renderers, den die Bild-Effekte anfassen — mehr brauchen sie
 * nicht. Nur vier der zehn Funktionen benutzen ihn.
 */
export interface ImageEffectHost {
    /** Globaler Schatten-Aufhell-Uniform (0 = voller Schatten). Wird von einer
     *  Praesentations-Option getrieben; Default {value:0} = kein Aufhellen. */
    shadowLiftUniform: { value: number };
    /** URL -> aufbereitete Graustufen-Quelle fuer custom-Masken. */
    maskSourceCache: { [url: string]: HTMLCanvasElement };
    /** URL -> laeuft gerade ein Ladevorgang (verhindert Doppel-Load). */
    maskTextureLoading: { [url: string]: boolean };
    /** Kantenlaenge der Masken-Leinwand (Default 2048; der Renderer setzt
     *  Touch 512 / sonst 1024, s. RendererWebGL.maskCanvasSize). Kachel-Vorschauen
     *  (RendererProps.maxTextureWidth gesetzt) waehlen kleiner: eine ANIMIERTE
     *  Maske malt die Leinwand pro Schritt neu und laedt sie KOMPLETT auf die
     *  GPU — 2048² sind 16 MB pro Frame und wuergten die Galerie-Hover-Kachel
     *  ab (Jans „Maske laeuft nicht ganz durch", 08.08.2026). */
    maskCanvasSize?: number;
}


export function injectShadowLift(
    host: ImageEffectHost,mat: TODOAny) {
  if (!mat || (mat.userData && mat.userData.__shadowLiftInjected)) {
    return
  }
  const prev = mat.onBeforeCompile
  mat.onBeforeCompile = (shader: TODOAny, renderer: TODOAny) => {
    if (typeof prev === 'function') {
      prev.call(mat, shader, renderer)
    }
    shader.uniforms.uShadowLift = host.shadowLiftUniform
  }
  mat.userData = mat.userData || {}
  mat.userData.__shadowLiftInjected = true
  // force a recompile so the newly-wired uniform takes effect
  mat.needsUpdate = true
}

// Traverse a (re)built 3D object and inject the shadow-lift uniform into every
// shadow-RECEIVING mesh material, so primitives, imported models and any
// material swapped in by updateMaterial all respond to the slider. Non-receivers
// and unlit materials are skipped (nothing to lighten there).

// ── Safari-Rueckfall fuer Canvas-Unschaerfe ─────────────────────────────────
// `ctx.filter = 'blur(…)'` unterstuetzt iOS-Safari erst ab 18 — davor wird die
// Zuweisung STILL ignoriert: die Silhouette bleibt scharf und Jans weiche
// Schlagschatten sind auf dem iPhone ploetzlich harte Kanten (Fund 31.07.2026;
// am Desktop-Chrome unauffaellig). Einmalig erkennen statt raten.
//
// WICHTIG — FUNKTIONAL pruefen, nicht nur parsen (Jans Fund 02.08.2026,
// "Images lassen sich in Safari nicht weichzeichnen"): Desktop-Safari NIMMT
// den Wert an (`ctx.filter` liest 'blur(1px)' zurueck), WENDET ihn beim
// Zeichnen aber nicht an. Der reine Parse-Test lieferte dort ein falsches
// "Ja" — Ergebnis: weder echter Blur noch unser Skalier-Rueckfall. Deshalb:
// wirklich zeichnen und nachmessen, ob eine Kante verwaschen wurde.
let filterSupport: boolean | null = null
function canvasBlurFilterSupported(): boolean {
  if (filterSupport !== null) return filterSupport
  try {
    const c = document.createElement('canvas')
    c.width = 16
    c.height = 16
    const ctx = c.getContext('2d', { willReadFrequently: true })
    if (!ctx) return (filterSupport = false)
    ctx.filter = 'blur(2px)'
    if (ctx.filter === 'none') return (filterSupport = false)
    ctx.fillStyle = '#fff'
    ctx.fillRect(4, 4, 8, 8)
    const d = ctx.getImageData(0, 0, 16, 16).data
    // Mit wirksamem Blur laeuft Alpha weich aus (Zwischenwerte); ohne ist
    // jedes Pixel entweder voll deckend oder voll leer.
    let soft = false
    for (let i = 3; i < d.length; i += 4) {
      if (d[i] > 8 && d[i] < 247) {
        soft = true
        break
      }
    }
    filterSupport = soft
  } catch {
    filterSupport = false
  }
  return filterSupport
}

// Der eigentliche Rueckfall-Blur (3-Pass-Box-Blur ≈ Gauss, premultipliziert)
// liegt in canvasBlur.ts — geteilt mit Jans Editor-Engine. Der fruehere
// Skalier-Trick (halbieren + hochskalieren) war Jans Auge zu grob.

// Backt die verwaschene Kopie in ein (verkleinertes) Canvas. Mit `reuse` wird
// DASSELBE Canvas neu bemalt statt eines neu anzulegen — der animierte Blur
// backt bei jedem ganzzahligen Zwischenwert neu, und Anlage + Verwurf von
// Canvas und Textur pro Schritt waren der Alloc-/GC-Anteil des Blur-Ruckelns
// (Jans Fund 06.08.2026). Das Setzen von width/height leert das Canvas samt
// Kontext-Zustand auch bei unverändertem Wert — eingebautes clearRect.
// Nur der Skalier-Rückfall alter iOS-Safaris liefert ein NEUES Canvas.
export function bakeBlurredCanvas(
  img: HTMLImageElement,
  blur: number,
  width: number,
  height: number,
  feather: boolean,
  reuse?: HTMLCanvasElement,
  /** Leinwand-Deckel fuer Kachel-Vorschauen (Default 1400) — ein ANIMIERTER
   *  Blur backt pro ganzzahligem Zwischenwert neu und laedt die Leinwand
   *  komplett zur GPU; 1400 in einer 230-px-Kachel war der verbliebene
   *  Ruckel-Treiber (Jan 08.08.2026). */
  maxSeite?: number
): HTMLCanvasElement {
  const f = Math.min(1, (maxSeite ?? 1400) / Math.max(width, height))
  const bw = Math.max(1, Math.round(width * f))
  const bh = Math.max(1, Math.round(height * f))
  const r = blur * f
  const useFilter = canvasBlurFilterSupported()
  let canvas =
    reuse && useFilter && reuse.width === bw && reuse.height === bh
      ? reuse
      : document.createElement('canvas')
  canvas.width = bw
  canvas.height = bh
  const ctx = canvas.getContext('2d')!
  if (useFilter) ctx.filter = `blur(${r}px)`
  // IMMER randlos-übergroß zeichnen, damit der Blur an der Kante echte Pixel
  // sieht. Früher schrumpfte der feather-Zweig das Bild stattdessen um
  // `Radius+2 px` nach innen (Inset) — beim Scharf/Unscharf-Wechsel sprang
  // das Bild dadurch sichtbar kleiner, und während einer Blur-ANIMATION
  // schrumpfte es kontinuierlich weiter: eine zweite, ungewollte
  // Zoom-Bewegung über der echten (Jans „kurz doppelt gezoomt", 08.08.2026,
  // Template „Zoom & Blur"). Die weiche Kante entsteht jetzt unten per
  // Transparenz-Verlauf — der Bildmaßstab bleibt exakt der der scharfen
  // Textur.
  const pad = Math.ceil(r) + 2
  ctx.drawImage(img, -pad, -pad, bw + pad * 2, bh + pad * 2)
  // iOS-Safari < 18: ctx.filter wurde oben still ignoriert -> Skalier-Rueckfall
  if (!useFilter) canvas = blurCanvasFallback(canvas, r)
  if (feather) {
    // Weiche Kante: nach innen auslaufende Transparenz (destination-out mit
    // Linearverlauf) über dieselbe Breite, die früher das Inset hatte. Läuft
    // NACH dem Fallback, damit sie auch dort auf der fertigen Fläche liegt.
    const fctx = canvas.getContext('2d')!
    const d = Math.min(Math.ceil(r) + 2, Math.floor(Math.min(bw, bh) / 2) - 1)
    if (d > 0) {
      fctx.filter = 'none'
      fctx.globalCompositeOperation = 'destination-out'
      const blende = (
        x0: number, y0: number, x1: number, y1: number,
        rx: number, ry: number, rw: number, rh: number
      ) => {
        const g = fctx.createLinearGradient(x0, y0, x1, y1)
        g.addColorStop(0, 'rgba(0,0,0,1)')
        g.addColorStop(1, 'rgba(0,0,0,0)')
        fctx.fillStyle = g
        fctx.fillRect(rx, ry, rw, rh)
      }
      blende(0, 0, 0, d, 0, 0, bw, d) // oben
      blende(0, bh, 0, bh - d, 0, bh - d, bw, d) // unten
      blende(0, 0, d, 0, 0, 0, d, bh) // links
      blende(bw, 0, bw - d, 0, bw - d, 0, d, bh) // rechts
      fctx.globalCompositeOperation = 'source-over'
    }
  }
  return canvas
}

export function makeBlurredTexture(
  img: HTMLImageElement,
  blur: number,
  width: number,
  height: number,
  feather: boolean
): THREE.CanvasTexture {
  const canvas = bakeBlurredCanvas(img, blur, width, height, feather)
  const tex = new THREE.CanvasTexture(canvas)
  // the canvas holds sRGB pixels like the sharp source texture — without this
  // tag three treats them as linear and the blurred image renders brighter
  tex.colorSpace = THREE.SRGBColorSpace
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  return tex
}

// Optional image mask: show the image only through a shape (the "circle"
// preset or an uploaded grayscale/alpha mask), applied as the material's
// alphaMap. The mask window is moved/scaled over the (fixed) image via Mask
// X / Y / Size. NOTE: three r139 shares one UV transform across a material's
// textures (driven by `map`), so alphaMap.offset/repeat is ignored — the mask
// transform is therefore *baked* into a per-layer canvas each time it changes.

export function buildMaskSourceCanvas(img: HTMLImageElement): HTMLCanvasElement {
  const w = Math.max(1, img.width)
  const h = Math.max(1, img.height)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, w, h)
  try {
    const data = ctx.getImageData(0, 0, w, h)
    const px = data.data
    let hasAlpha = false
    for (let i = 3; i < px.length; i += 4) {
      if (px[i] < 250) {
        hasAlpha = true
        break
      }
    }
    for (let i = 0; i < px.length; i += 4) {
      const v = hasAlpha
        ? px[i + 3]
        : 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]
      px[i] = px[i + 1] = px[i + 2] = v
      px[i + 3] = 255
    }
    ctx.putImageData(data, 0, 0)
  } catch (error) {
    // tainted canvas (CORS) — fall back to the raw image as the mask
    // eslint-disable-next-line no-console
    console.warn('Mask processing failed (CORS?), using raw image', error)
  }
  return canvas
}

// "Show Camera": a wireframe frustum of the scripted presentation camera so
// you can see where the camera sits / what it frames — most useful while
// orbiting (Use Orbit Controls). The stand-in camera always stays at the
// scripted pose, independent of the orbited view camera.

export function getCustomMaskSource(
    host: ImageEffectHost,url: string): HTMLCanvasElement | undefined {
  if (host.maskSourceCache[url]) return host.maskSourceCache[url]
  if (host.maskTextureLoading[url]) return undefined
  host.maskTextureLoading[url] = true
  const loader = new THREE.ImageLoader()
  loader.setCrossOrigin('anonymous')
  loader.load(
    url,
    (img) => {
      host.maskSourceCache[url] = buildMaskSourceCanvas(img)
      host.maskTextureLoading[url] = false
    },
    undefined,
    () => {
      host.maskTextureLoading[url] = false
    }
  )
  return undefined
}

export function drawMaskShape(
  ctx: CanvasRenderingContext2D,
  shape: string,
  cx: number,
  cy: number,
  rx: number,
  ry: number
) {
  const TAU = Math.PI * 2
  // regular polygon, first vertex pointing up, as a closed path
  const polygon = (sides: number) => {
    ctx.beginPath()
    for (let i = 0; i < sides; i += 1) {
      const a = -Math.PI / 2 + (i / sides) * TAU
      const x = cx + rx * Math.cos(a)
      const y = cy + ry * Math.sin(a)
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.closePath()
  }

  switch (shape) {
    case 'square':
      ctx.beginPath()
      ctx.rect(cx - rx, cy - ry, rx * 2, ry * 2)
      break
    case 'rounded': {
      const r = Math.min(rx, ry) * 0.28
      const x0 = cx - rx
      const y0 = cy - ry
      const w = rx * 2
      const h = ry * 2
      ctx.beginPath()
      ctx.moveTo(x0 + r, y0)
      ctx.arcTo(x0 + w, y0, x0 + w, y0 + h, r)
      ctx.arcTo(x0 + w, y0 + h, x0, y0 + h, r)
      ctx.arcTo(x0, y0 + h, x0, y0, r)
      ctx.arcTo(x0, y0, x0 + w, y0, r)
      ctx.closePath()
      break
    }
    case 'triangle':
      polygon(3)
      break
    case 'diamond':
      polygon(4)
      break
    case 'hexagon':
      polygon(6)
      break
    case 'star': {
      const spikes = 5
      const innerX = rx * 0.42
      const innerY = ry * 0.42
      ctx.beginPath()
      for (let i = 0; i < spikes * 2; i += 1) {
        const a = -Math.PI / 2 + (i / (spikes * 2)) * TAU
        const ex = i % 2 === 0 ? rx : innerX
        const ey = i % 2 === 0 ? ry : innerY
        const x = cx + ex * Math.cos(a)
        const y = cy + ey * Math.sin(a)
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      }
      ctx.closePath()
      break
    }
    case 'heart': {
      const t = cy - ry * 0.3 // top centre dip
      ctx.beginPath()
      ctx.moveTo(cx, t)
      ctx.bezierCurveTo(cx, cy - ry * 0.85, cx - rx, cy - ry * 0.85, cx - rx, cy - ry * 0.15)
      ctx.bezierCurveTo(cx - rx, cy + ry * 0.35, cx - rx * 0.5, cy + ry * 0.6, cx, cy + ry)
      ctx.bezierCurveTo(cx + rx * 0.5, cy + ry * 0.6, cx + rx, cy + ry * 0.35, cx + rx, cy - ry * 0.15)
      ctx.bezierCurveTo(cx + rx, cy - ry * 0.85, cx, cy - ry * 0.85, cx, t)
      ctx.closePath()
      break
    }
    case 'keyhole': {
      const headRx = rx * 0.62
      const headRy = ry * 0.62
      const headCy = cy - ry * 0.28
      const topW = headRx * 0.42
      const botW = headRx * 0.95
      const botY = cy + ry
      ctx.beginPath()
      // round head
      ctx.ellipse(cx, headCy, headRx, headRy, 0, 0, TAU)
      // tapered stem, overlapping the head so the two union into a keyhole
      ctx.moveTo(cx - topW, headCy)
      ctx.lineTo(cx + topW, headCy)
      ctx.lineTo(cx + botW, botY)
      ctx.lineTo(cx - botW, botY)
      ctx.closePath()
      break
    }
    case 'circle':
    default:
      ctx.beginPath()
      ctx.ellipse(cx, cy, rx, ry, 0, 0, TAU)
      break
  }
  ctx.fill()
}

// Loads an uploaded mask asset into a grayscale source canvas: a transparent
// PNG uses its alpha channel, an opaque (e.g. black/white) image uses its
// luminance. Cached by URL; returns undefined while still loading.

export function drawMaskInto(
  canvas: HTMLCanvasElement,
  shape: string,
  aspect: number,
  size: number,
  ox: number,
  oy: number,
  customSource?: HTMLCanvasElement
) {
  const W = canvas.width
  const H = canvas.height
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, W, H)
  // mask centre in canvas space (the CanvasTexture flips Y, so +Y = up means
  // a smaller canvas y)
  const cx = (0.5 + ox) * W
  const cy = (0.5 - oy) * H

  if (shape === 'custom') {
    if (customSource) {
      // size 1 -> die Maske passt ganz ins Bild und behält dabei IHR
      // Seitenverhältnis (vorher füllte sie die Fläche und wurde verzerrt,
      // s. maskenRechteck); scale + offset from there
      const r = maskenRechteck(
        customSource.width / Math.max(1, customSource.height),
        aspect,
        size,
      )
      const destW = W * r.breite
      const destH = H * r.hoehe
      ctx.drawImage(customSource, cx - destW / 2, cy - destH / 2, destW, destH)
    }
    return
  }

  // half-extents in canvas px, aspect-corrected so a "round" shape stays
  // round on the (possibly non-square) image plane
  const base = 0.5 * size
  const rx = aspect >= 1 ? (base / aspect) * W : base * W
  const ry = aspect >= 1 ? base * H : base * aspect * H
  ctx.fillStyle = '#fff'
  drawMaskShape(ctx, shape, cx, cy, rx, ry)
}

// Draws a white preset mask shape centred at (cx,cy) within the half-extents
// (rx,ry). Everything is one fill, so overlapping sub-paths union.

export function syncImageBlur(
  layerMesh: THREE.Mesh,
  content: ImageContent,
  blurAnim?: number,
  maxSeite?: number
) {
  if (Array.isArray(layerMesh.material)) return
  const material = layerMesh.material as THREE.MeshBasicMaterial
  if (!material) return
  // animated blur takes precedence; round so tiny interpolation steps don't
  // re-bake the texture every single frame.
  //
  // SAFARI-DROSSEL (Jans Fund 30.08.2026, "Mixed Media" ruckelt nur in
  // Safari): Ohne ctx.filter-Unterstuetzung laeuft jeder Bake als 3-Pass-
  // Box-Blur in purem JS — bei 9 Ebenen mit animierter Unschaerfe wuergte
  // das Safaris Hauptthread bei jedem Scroll-Schritt. Deshalb dort beim
  // ANIMIERTEN Blur: (a) Zwischenwerte auf 3er-Schritte quantisieren
  // (~3x weniger Bakes; verwaschene Zwischenstufen sieht niemand in
  // Bewegung), (b) in halber Aufloesung backen (~4x weniger Pixel je
  // Bake). Chrome & statischer Blur bleiben unangetastet.
  const cpuRueckfall = !canvasBlurFilterSupported()
  const sparmodus = cpuRueckfall && blurAnim !== undefined
  const blur =
    blurAnim !== undefined
      ? sparmodus
        ? Math.max(blurAnim > 0 ? 1 : 0, Math.round(blurAnim / 3) * 3)
        : Math.round(blurAnim)
      : content.blur ?? 0
  const curMap = material.map
  const blurTex = layerMesh.userData.blurTex as THREE.Texture | undefined
  // remember the sharp texture whenever the current map is not our blurred one
  if (curMap && curMap !== blurTex) {
    layerMesh.userData.sharpMap = curMap
  }
  const sharp = layerMesh.userData.sharpMap as THREE.Texture | undefined

  if (blur <= 0) {
    if (curMap === blurTex && sharp) {
      material.map = sharp
      material.needsUpdate = true
    }
    return
  }

  const img = sharp?.image as HTMLImageElement | undefined
  if (!img || !img.width) return
  // a mask defines the edge -> fill the plane edge-to-edge (no feather), so
  // the masked shape stays clean; otherwise feather the asset's own edge
  const masked = !!content.maskShape && content.maskShape !== 'none'
  const feather = !masked
  // Halbe Back-Aufloesung im Sparmodus; Modus in die Signatur, damit der
  // Wechsel animiert<->statisch sauber neu backt statt stale zu bleiben.
  const deckel = maxSeite ?? 1400
  const bakeDeckel = sparmodus ? Math.round(deckel / 2) : deckel
  const sig = `${blur}|${img.width}x${img.height}|${feather ? 'f' : 'h'}|${bakeDeckel}`
  if (
    !blurTex ||
    layerMesh.userData.blurSig !== sig ||
    layerMesh.userData.blurImg !== img
  ) {
    // Canvas und CanvasTexture WIEDERVERWENDEN, wenn nur der Blur-Wert
    // wechselt (animierter Blur: neuer Backvorgang pro ganzzahligem
    // Zwischenwert): neu bemalen + needsUpdate lädt in die bestehende
    // GPU-Textur, statt pro Schritt Canvas/Textur anzulegen und zu
    // verwerfen. Bei Bildwechsel (andere Größe möglich) bewusst frisch.
    const bisherCanvas =
      layerMesh.userData.blurImg === img
        ? (layerMesh.userData.blurCanvas as HTMLCanvasElement | undefined)
        : undefined
    const canvas = bakeBlurredCanvas(
      img,
      blur,
      content.width,
      content.height,
      feather,
      bisherCanvas,
      bakeDeckel
    )
    if (blurTex && canvas === layerMesh.userData.blurCanvas) {
      blurTex.needsUpdate = true
    } else {
      if (blurTex) blurTex.dispose()
      const tex = new THREE.CanvasTexture(canvas)
      // sRGB-Kennzeichnung wie die scharfe Quelle — sonst rendert die
      // verwaschene Kopie heller (siehe makeBlurredTexture)
      tex.colorSpace = THREE.SRGBColorSpace
      tex.minFilter = THREE.LinearFilter
      tex.magFilter = THREE.LinearFilter
      layerMesh.userData.blurTex = tex
      layerMesh.userData.blurCanvas = canvas
    }
    layerMesh.userData.blurSig = sig
    layerMesh.userData.blurImg = img
  }
  if (material.map !== layerMesh.userData.blurTex) {
    material.map = layerMesh.userData.blurTex
    material.needsUpdate = true
  }
}

// Bakes a blurred copy of the image into a (downscaled) canvas texture.
// feather=true draws the image inset so the blur softens the asset's own
// edges (out-of-focus look); feather=false draws it oversized so the blurred
// image fills the plane edge-to-edge (the mask then defines the edge).

export function syncImageMask(
    host: ImageEffectHost,
  layerMesh: THREE.Mesh,
  content: ImageContent,
  maskAnim?: Vector3Tuple
) {
  if (Array.isArray(layerMesh.material)) return
  const material = layerMesh.material as THREE.MeshBasicMaterial
  if (!material) return
  const shape = content.maskShape ?? 'none'

  if (shape === 'none') {
    if (layerMesh.userData.maskApplied) {
      material.alphaMap = null
      material.needsUpdate = true
      const ownTex = layerMesh.userData.maskTex as THREE.Texture | undefined
      if (ownTex) ownTex.dispose()
      layerMesh.userData.maskTex = undefined
      layerMesh.userData.maskCanvas = undefined
      layerMesh.userData.maskSig = undefined
      layerMesh.userData.maskApplied = false
    }
    return
  }

  // for custom masks we need the processed grayscale source canvas
  let customSource: HTMLCanvasElement | undefined
  if (shape === 'custom') {
    if (!content.maskAsset?.url) return
    customSource = getCustomMaskSource(host, content.maskAsset.url)
    if (!customSource) return // still loading
  }

  const aspect =
    (layerMesh.userData.width || content.width || 1) /
    (layerMesh.userData.height || content.height || 1)
  // animated mask transform [x, y, size] takes precedence; fall back to the
  // static content values when the channel is not wired up
  const ox = maskAnim ? maskAnim[0] : content.maskOffsetX ?? 0
  const oy = maskAnim ? maskAnim[1] : content.maskOffsetY ?? 0
  const size = Math.max(0.05, maskAnim ? maskAnim[2] : content.maskZoom ?? 1)

  const sig = `${shape}:${aspect.toFixed(3)}:${
    content.maskAsset?.url ?? ''
  }:${size.toFixed(3)}:${ox.toFixed(3)}:${oy.toFixed(3)}`

  let canvas = layerMesh.userData.maskCanvas as HTMLCanvasElement | undefined
  let tex = layerMesh.userData.maskTex as THREE.CanvasTexture | undefined
  if (!canvas) {
    canvas = document.createElement('canvas')
    // crisp edge even when the masked image is shown large / on retina.
    // NOTE: do NOT resize this canvas later — resizing a canvas that backs a
    // live WebGL texture can leave stale pixels (a ghost/second shape).
    // Kachel-Vorschauen fahren kleiner (s. ImageEffectHost.maskCanvasSize).
    const seite = Math.max(256, Math.min(2048, host.maskCanvasSize ?? 2048))
    canvas.width = seite
    canvas.height = seite
    layerMesh.userData.maskCanvas = canvas
  }
  if (layerMesh.userData.maskSig !== sig) {
    drawMaskInto(canvas, shape, aspect, size, ox, oy, customSource)
    if (!tex) {
      tex = new THREE.CanvasTexture(canvas)
      tex.minFilter = THREE.LinearFilter
      tex.magFilter = THREE.LinearFilter
      layerMesh.userData.maskTex = tex
    } else {
      tex.needsUpdate = true
    }
    layerMesh.userData.maskSig = sig
  }

  if (material.alphaMap !== tex) {
    material.alphaMap = tex!
    material.transparent = true
    material.needsUpdate = true
  }

  layerMesh.userData.maskApplied = true
}

// Bakes the mask shape into `canvas` at the requested position/size. The
// canvas maps 1:1 across the image plane (UV identity), so the geometry must
// be expressed in plane-UV space and corrected for the image aspect so a
// circle stays a circle. Outside the shape is black -> fully masked out.

/**
 * Wie gross wird der Schlagschatten gebacken?
 *
 * Antwort: so gross wie die FLAECHE auf der Buehne — nicht so gross wie der
 * Platz in der Vorlage (Jans Fund 24.09.2026, KI-Scrolly). Beides ist oft
 * dasselbe, bei KI-Scrollys aber nicht: gemessen an scrolly/68db3454 stand im
 * Platz 2048x3072 (hoch), waehrend die Flaeche 5461x3072 (quer) mass. Der
 * Schatten lag dadurch quer zum Bild und wirkte wie der eines anderen Bildes.
 *
 * `flaeche` kommt aus der Geometrie des Meshes — das ist per Definition das,
 * was auf der Buehne steht. Fehlt sie (kein Mesh, leere Geometrie), bleibt es
 * beim bisherigen Verhalten.
 */
export function schattenFlaeche(
  flaeche: { breite: number; hoehe: number } | null | undefined,
  content: { width: number; height: number },
): { width: number; height: number } {
  const b = flaeche?.breite ?? 0
  const h = flaeche?.hoehe ?? 0
  return {
    width: b > 0 ? b : content.width,
    height: h > 0 ? h : content.height,
  }
}

/**
 * Der Zwischenspeicher-Schluessel des Fake-Schlagschattens.
 *
 * Er muss ALLES enthalten, was das gebackene Schattenbild bestimmt — sonst
 * bleibt ein alter Bake stehen. Genau das war Jans Fund 24.09.2026: der
 * Schluessel kannte das BILD nicht, nur die Masse der Ebene. Beim Bildtausch
 * in einer Vorlage bleiben die Masse aber gleich (das ist ja der Platz), also
 * wurde der Schatten des alten Bildes weiterbenutzt — ein Hochformat in einem
 * Querformat-Platz behielt den Schatten quer ueber die ganze Flaeche.
 *
 * `bildKennung` ist bewusst die Adresse der Textur, die GERADE haengt (nicht
 * die aus dem Content): so beschreibt der Schluessel wirklich das, was
 * gebacken wurde.
 *
 * Der VERSATZ gehoert NICHT hinein — der wird in 3D auf die Ebene angewandt,
 * nicht ins Bild gebacken.
 */
export function schattenSchluessel(teile: {
  width: number
  height: number
  blur: number
  opacity: number
  color: { r: number; g: number; b: number }
  maskSig: string
  bildKennung: string
}): string {
  const { width, height, blur, opacity, color: c, maskSig, bildKennung } = teile
  return `${width}x${height}|${blur}|${opacity}|${c.r},${c.g},${c.b}|${maskSig}|${bildKennung}`
}

/**
 * STUFENLEITER DER WEICHHEIT fuer ANIMIERTE Schatten (Jans Wunsch 28.09.2026,
 * Folge des iPhone-Funds vom 27.09.: eine Weichheits-Animation backte den
 * Schatten in jedem Bild neu — Bild einfaerben, weichzeichnen, Textur
 * hochladen). Statt jeden Zwischenwert zu backen, gibt es feste Stufen (etwa
 * 6 % Abstand); jede wird EINMAL gebacken und am Bild zwischengespeichert.
 * Zwischen zwei Stufen wird per UEBERBLENDUNG zweier Schattenflaechen
 * gemischt — pro Bild nur zwei Deckkraft-Werte, kein Backen. Nicht animierte
 * Schatten backen weiter exakt (unveraenderter Look).
 */
export const WEICHHEIT_STUFEN = [
  0, 2, 3, 4, 5, 6, 7, 8, 10, 12, 14, 17, 20, 24, 28, 33, 39, 46, 54, 63, 74, 86, 100, 120,
]

/** Untere/obere Stufe um `blur` und das Gewicht der oberen (0 = ganz unten). */
export function weichheitStufen(blur: number): { lo: number; hi: number; w: number } {
  const b = Math.max(0, blur)
  const max = WEICHHEIT_STUFEN[WEICHHEIT_STUFEN.length - 1]
  if (b >= max) return { lo: max, hi: max, w: 0 }
  let i = 0
  while (WEICHHEIT_STUFEN[i + 1] <= b) i++
  const lo = WEICHHEIT_STUFEN[i]
  const hi = WEICHHEIT_STUFEN[i + 1]
  const w = hi === lo ? 0 : (b - lo) / (hi - lo)
  return { lo, hi, w }
}

/**
 * Hoechstens so viele gebackene Stufen je Bild im Speicher (aelteste fliegen).
 * Reicht fuer die ganze Leiter, damit das Vorwaermen nichts verdraengt, was
 * es gerade erst gebacken hat.
 */
const SCHATTEN_CACHE_MAX = WEICHHEIT_STUFEN.length + 2

/**
 * AUFLOESUNG NACH WEICHHEIT (Jan 28.09.2026, „Punkt 1"): Ein weicher Schatten
 * braucht keine 1200 px — ab Weichheit 20 reicht die halbe Kante (ein Viertel
 * der Pixelarbeit, im weichgezeichneten Ergebnis nicht zu sehen). Masken
 * backen wie bisher mit 512.
 */
export function schattenAufloesung(blurStufe: number, mitMaske: boolean): number {
  if (mitMaske) return 512
  return blurStufe >= 20 ? 600 : 1200
}

/** Leerlauf-Schritt fuers Vorwaermen: requestIdleCallback, sonst kurzer Timer. */
const imLeerlauf = (cb: () => void) => {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(() => cb(), { timeout: 500 })
  else setTimeout(cb, 32)
}

interface Gebacken {
  texture: THREE.CanvasTexture
  cw: number
  ch: number
  f: number
}

export function syncImageShadow(
  layerMesh: THREE.Mesh,
  content: ImageContent,
  // interpolated Softness/Strength while their channel animates; when a value
  // changes the cache key below changes and the silhouette is re-baked
  anim?: {
    blur?: number
    opacity?: number
    /**
     * Spanne der Weichheits-Keyframes (min, max): dann werden alle Stufen
     * darin im Leerlauf VORGEWAERMT, damit auch der erste Durchlauf der
     * Animation nicht stockt (Jan 28.09.2026).
     */
    blurRange?: [number, number]
  }
) {
  const SHADOW_NAME = '__fakeShadow'
  const SHADOW2_NAME = '__fakeShadow2'
  const existing = layerMesh.children.find(
    (c) => c.name === SHADOW_NAME
  ) as THREE.Mesh | undefined

  // real (light-driven) shadows take precedence — never show the flat fake
  // drop shadow at the same time (it would show through 3D objects)
  const realShadowsOn = !!content.castShadowReal || !!content.receiveShadowReal

  const cacheLeeren = () => {
    const cache = layerMesh.userData.schattenBakes as Map<string, Gebacken> | undefined
    cache?.forEach((g) => g.texture.dispose())
    cache?.clear()
  }
  const schattenEntfernen = (mesh: THREE.Mesh) => {
    mesh.parent?.remove(mesh)
    // Texturen gehoeren dem Cache (oder werden unten mit ihm entsorgt) — hier
    // nur Material und Geometrie
    ;(mesh.material as THREE.Material).dispose()
    ;(mesh as TODOAny).geometry?.dispose()
  }

  if (!content.shadowEnabled || realShadowsOn) {
    if (existing) {
      const zweite = existing.children.find((c) => c.name === SHADOW2_NAME) as THREE.Mesh | undefined
      if (zweite) schattenEntfernen(zweite)
      schattenEntfernen(existing)
      cacheLeeren()
    }
    return
  }

  const mapImage = (layerMesh.material as THREE.MeshBasicMaterial).map
    ?.image as TODOAny
  // VIDEO-Layer (map = VideoTexture, .image = <video>): Silhouette ist das
  // volle Rechteck — pro Frame die Videobilder nachzuzeichnen waere sinnlos
  // teuer, und ein Video fuellt seine Flaeche ohnehin deckend (Jans Wunsch
  // 02.08.2026: Schlagschatten auch fuer Videos). Chroma-Key-Videos laufen
  // ueber ein ShaderMaterial ohne .map und bleiben aussen vor.
  const isVideo =
    typeof HTMLVideoElement !== 'undefined' && mapImage instanceof HTMLVideoElement
  const image = isVideo ? undefined : (mapImage as HTMLImageElement | undefined)
  // when a mask is active, the shadow follows the mask shape (baked per-layer
  // in syncImageMask) instead of the full image rectangle
  const maskCanvas = layerMesh.userData.maskApplied
    ? (layerMesh.userData.maskCanvas as HTMLCanvasElement | undefined)
    : undefined
  if (!maskCanvas && !isVideo && (!image || !image.width)) return

  // DIE GROESSE DER FLAECHE, NICHT DIE DES PLATZES (Jans Fund 24.09.2026,
  // KI-Scrolly): Der Schatten wurde aus `content.width/height` gebacken — das
  // ist der Platz in der Vorlage. Die Bildflaeche selbst folgt aber dem
  // Seitenverhaeltnis der Datei (calcWidth/calcHeight bzw. bildPlaneSize).
  // Bei KI-Scrollys gehen beide weit auseinander, gemessen an
  // scrolly/68db3454: Platz 2048x3072 (hoch), Flaeche 5461x3072 (quer) — der
  // Schatten lag also quer zum Bild und wirkte wie der eines anderen Bildes.
  //
  // Gelesen wird die Flaeche direkt aus der Geometrie: das IST die Flaeche,
  // die auf der Buehne steht — damit koennen Schatten und Bild gar nicht mehr
  // auseinanderlaufen, egal welcher Weg die Masse gesetzt hat.
  const geo = layerMesh.geometry as THREE.BufferGeometry | undefined
  if (geo && !geo.boundingBox) geo.computeBoundingBox()
  const bb = geo?.boundingBox
  const { width, height } = schattenFlaeche(
    bb ? { breite: bb.max.x - bb.min.x, hoehe: bb.max.y - bb.min.y } : null,
    content,
  )
  const blur = anim?.blur ?? content.shadowBlur ?? 25
  // DECKKRAFT NICHT MEHR EINBACKEN (Jans iPhone-Fund 27.09.2026: Bilder mit
  // animiertem Schlagschatten ruckelten, Desktop-Safari lief sauber). War die
  // Deckkraft Teil von Bake und Schluessel, loeste JEDER Frame einer
  // Deckkraft-Animation ein neues Schattenbild aus (Bild einfaerben,
  // weichzeichnen, Textur hochladen) — fuers iPhone zu viel. Jetzt wird die
  // Silhouette mit voller Deckkraft gebacken und die (animierte) Deckkraft nur
  // als Materialwert aufgelegt (s. renderLayer: Bild-Deckkraft × diese).
  // Ergebnis identisch: Alpha skaliert linear, die Farbe ist ueberall gleich.
  const opacity = anim?.opacity ?? content.shadowOpacity ?? 0.25
  const BAKE_DECKKRAFT = 1
  const c = content.shadowColor ?? { r: 0, g: 0, b: 0, a: 1 }
  // offset is applied via the plane position (see renderLayer), so it is NOT
  // part of the bake key — only shape/blur/colour (and the mask shape, which
  // changes as the mask moves/scales) trigger a rebake
  const maskSig = maskCanvas ? (layerMesh.userData.maskSig ?? '') : ''
  // WELCHES BILD wurde gebacken? (Jans Fund 24.09.2026)
  //
  // Der Schluessel kannte bisher nur Groesse, Weichheit, Deckkraft, Farbe und
  // die Maske — NICHT das Bild selbst. Tauscht man in einer Vorlage das Bild,
  // bleiben Breite und Hoehe der Ebene aber gleich (das ist ja der Platz).
  // Der Schluessel blieb damit unveraendert, der alte Bake wurde behalten —
  // und ein Hochformat in einem Querformat-Platz behielt den Schlagschatten
  // des ALTEN Bildes, quer ueber die ganze Flaeche.
  //
  // Genommen wird die Adresse des Bildes, das GERADE in der Textur haengt
  // (nicht die aus dem Content): so beschreibt der Schluessel wirklich das,
  // was gebacken wurde. Haengt beim Tausch noch die alte Textur, wird nach
  // dem Nachladen sauber neu gebacken statt einmal falsch zu verharren.
  const bildKennung = isVideo
    ? 'video'
    : ((image as { currentSrc?: string; src?: string } | undefined)?.currentSrc ??
       (image as { src?: string } | undefined)?.src ??
       '')
  // Schluessel OHNE Weichheit: aendert sich Form, Farbe, Maske oder Bild,
  // sind alle gebackenen Stufen hinfaellig
  const basisKey = schattenSchluessel({
    width,
    height,
    blur: -1,
    opacity: BAKE_DECKKRAFT,
    color: c,
    maskSig,
    bildKennung,
  })
  const keyFuer = (b: number) => `${basisKey}|${b}`

  if (layerMesh.userData.schattenBasisKey !== basisKey) {
    cacheLeeren()
    layerMesh.userData.schattenBasisKey = basisKey
  }
  const cache: Map<string, Gebacken> =
    (layerMesh.userData.schattenBakes as Map<string, Gebacken> | undefined) ??
    (layerMesh.userData.schattenBakes = new Map())

  // ANIMIERT (Kanal aktiv): Stufenleiter + Ueberblendung. STATISCH: exakt.
  const animiert = anim?.blur !== undefined
  const stufen = animiert ? weichheitStufen(blur) : { lo: blur, hi: blur, w: 0 }

  // bake a blurred, tinted silhouette. For a plain image this is the image's
  // own shape (opaque JPEG rectangle or transparent-PNG logo); for a masked
  // image it is the mask shape. The offset is applied later in 3D. The mask
  // shadow bakes at a lower resolution (it is blurred anyway) so it stays
  // cheap while the mask animates.
  const backen = (blurStufe: number): Gebacken | null => {
    const f = Math.min(1, schattenAufloesung(blurStufe, !!maskCanvas) / Math.max(width, height))
    const bw = Math.max(1, Math.round(width * f))
    const bh = Math.max(1, Math.round(height * f))
    const bpad = Math.ceil(blurStufe * f * 3) + 8
    const cw = bw + bpad * 2
    const ch = bh + bpad * 2

    const silhouette = document.createElement('canvas')
    silhouette.width = cw
    silhouette.height = ch
    const sctx = silhouette.getContext('2d')!
    if (maskCanvas) {
      // the mask canvas is a white shape on black; turn its luminance into the
      // shadow's alpha and tint the result to the shadow colour
      sctx.drawImage(maskCanvas, bpad, bpad, bw, bh)
      const data = sctx.getImageData(0, 0, cw, ch)
      const px = data.data
      for (let i = 0; i < px.length; i += 4) {
        const lum = px[i]
        px[i] = c.r
        px[i + 1] = c.g
        px[i + 2] = c.b
        px[i + 3] = Math.round(lum * BAKE_DECKKRAFT)
      }
      sctx.putImageData(data, 0, 0)
    } else if (isVideo) {
      // Video: volle Rechteck-Silhouette direkt in Schattenfarbe
      sctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${BAKE_DECKKRAFT})`
      sctx.fillRect(bpad, bpad, bw, bh)
    } else {
      // draw the image, then tint it to the shadow colour — source-in keeps the
      // colour only where the image is opaque (full rect for a JPEG, the logo
      // shape for a transparent PNG)
      try {
        sctx.drawImage(image!, bpad, bpad, bw, bh)
      } catch (error) {
        // cross-origin image without CORS headers would taint the canvas
        // eslint-disable-next-line no-console
        console.warn('Drop shadow bake failed (CORS?)', error)
        return null
      }
      sctx.globalCompositeOperation = 'source-in'
      sctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${BAKE_DECKKRAFT})`
      sctx.fillRect(0, 0, cw, ch)
    }

    // 2) blur the tinted silhouette
    let canvas: HTMLCanvasElement
    if (canvasBlurFilterSupported()) {
      canvas = document.createElement('canvas')
      canvas.width = cw
      canvas.height = ch
      const ctx = canvas.getContext('2d')!
      ctx.filter = `blur(${blurStufe * f}px)`
      ctx.drawImage(silhouette, 0, 0)
    } else {
      // iOS-Safari < 18 (kein ctx.filter): Skalier-Rueckfall — sonst bleibt die
      // Silhouette scharf und der weiche Schlagschatten wird zur harten Kante
      canvas = blurCanvasFallback(silhouette, blurStufe * f)
    }

    const texture = new THREE.CanvasTexture(canvas)
    // sRGB tag like the image textures — untagged, the tinted silhouette
    // renders washed-out (a red shadow turns salmon, grey tones lighten)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    return { texture, cw, ch, f }
  }

  /** Gebackene Stufe aus dem Cache, sonst einmal backen und merken. */
  const stufeHolen = (blurStufe: number): Gebacken | null => {
    const k = keyFuer(blurStufe)
    const da = cache.get(k)
    if (da) return da
    const neu = backen(blurStufe)
    if (!neu) return null
    if (cache.size >= SCHATTEN_CACHE_MAX) {
      const aelteste = cache.keys().next().value
      if (aelteste !== undefined) {
        cache.get(aelteste)?.texture.dispose()
        cache.delete(aelteste)
      }
    }
    cache.set(k, neu)
    return neu
  }

  const neuesMaterial = (g: Gebacken) =>
    new THREE.MeshBasicMaterial({
      map: g.texture,
      transparent: true,
      depthWrite: false,
      // TIEFENPRUEFUNG AN (Jans Fund 03.08.2026: "der Schlagschatten hat
      // Einfluss auf die 3D-Objekte, die vor dem Video liegen"). Mit
      // depthTest:false ignorierte die Schatten-Flaeche den Tiefenpuffer und
      // uebermalte alles, was vorher gezeichnet wurde — also gerade die opaken
      // 3D-Objekte (die im Opaque-Pass VOR den transparenten Ebenen laufen).
      // Mit an ist das Verhalten geometrisch korrekt: 3D-Objekte DAVOR
      // verdecken den Schatten, 3D-Objekte dahinter nicht.
      //
      // Fuer reine 2D-Szenen aendert sich nichts: Bild-, Video- und
      // SVG-Ebenen zeichnen mit depthWrite:false, hinterlassen also keine
      // Tiefe, an der der Schatten haengenbleiben koennte. Auch der Schatten
      // selbst schreibt weiterhin keine Tiefe (depthWrite:false) und verdeckt
      // damit nie etwas dahinter.
      depthTest: true,
    })

  /** Flaeche auf eine gebackene Stufe setzen: Textur tauschen, Geometrie nur bei anderer Groesse. */
  const flaecheSetzen = (mesh: THREE.Mesh, g: Gebacken, k: string) => {
    if (mesh.userData.shadowKey === k) return
    const mat = mesh.material as THREE.MeshBasicMaterial
    if (mat.map !== g.texture) {
      mat.map = g.texture
      mat.needsUpdate = true
    }
    // canvas px -> world units (the image occupies bw×bh of it at scale f)
    const masse = `${g.cw}|${g.ch}|${g.f}`
    if (mesh.userData.schattenMasse !== masse) {
      ;(mesh.geometry as THREE.BufferGeometry)?.dispose()
      mesh.geometry = new THREE.PlaneGeometry(g.cw / g.f, g.ch / g.f)
      mesh.userData.schattenMasse = masse
    }
    mesh.userData.shadowKey = k
  }

  const loKey = keyFuer(stufen.lo)
  const lo = existing?.userData.shadowKey === loKey ? null : stufeHolen(stufen.lo)
  if (!existing && !lo) return

  // Hauptflaeche (untere Stufe)
  let haupt = existing
  if (!haupt) {
    haupt = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), neuesMaterial(lo!))
    haupt.name = SHADOW_NAME
    // always draw before the image so transparent sorting puts it behind
    haupt.renderOrder = (layerMesh.renderOrder || 0) - 1
    haupt.position.z = -1
    layerMesh.add(haupt)
  }
  if (lo) flaecheSetzen(haupt, lo, loKey)
  // Deckkraft = (animierte) Schatten-Deckkraft × Anteil dieser Stufe;
  // renderLayer legt die Bild-Deckkraft obendrauf
  haupt.userData.schattenDeckkraft = opacity * (1 - stufen.w)

  // Zweite Flaeche (obere Stufe) nur waehrend der Ueberblendung
  let zweite = haupt.children.find((c) => c.name === SHADOW2_NAME) as THREE.Mesh | undefined
  if (stufen.w > 0) {
    const hiKey = keyFuer(stufen.hi)
    const hi = zweite?.userData.shadowKey === hiKey ? null : stufeHolen(stufen.hi)
    if (!zweite && hi) {
      zweite = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), neuesMaterial(hi))
      zweite.name = SHADOW2_NAME
      zweite.renderOrder = haupt.renderOrder
      haupt.add(zweite)
    }
    if (zweite) {
      if (hi) flaecheSetzen(zweite, hi, hiKey)
      zweite.visible = true
      zweite.userData.schattenDeckkraft = opacity * stufen.w
    }
  } else if (zweite) {
    zweite.visible = false
    zweite.userData.schattenDeckkraft = 0
  }

  // VORWAERMEN (Jan 28.09.2026): Bei animierter Weichheit alle Stufen der
  // Keyframe-Spanne im Leerlauf backen, eine je Schritt — dann stockt auch
  // der erste Durchlauf nicht. Bricht ab, sobald Bild/Form wechseln
  // (basisKey) oder die Ebene aus der Szene ist.
  if (animiert && anim?.blurRange && !layerMesh.userData.schattenVorwaermer) {
    const [mn, mx] = anim.blurRange
    const von = weichheitStufen(Math.min(mn, mx)).lo
    const bis = weichheitStufen(Math.max(mn, mx)).hi
    const naechsteOffene = () =>
      WEICHHEIT_STUFEN.find((s) => s >= von && s <= bis && !cache.has(keyFuer(s)))
    if (naechsteOffene() !== undefined) {
      const generation = basisKey
      layerMesh.userData.schattenVorwaermer = true
      const schritt = () => {
        if (layerMesh.userData.schattenBasisKey !== generation || !layerMesh.parent) {
          layerMesh.userData.schattenVorwaermer = false
          return
        }
        const s = naechsteOffene()
        if (s === undefined) {
          layerMesh.userData.schattenVorwaermer = false
          return
        }
        stufeHolen(s)
        imLeerlauf(schritt)
      }
      imLeerlauf(schritt)
    }
  }
}

// Per-layer depth-of-field blur: swap the image's sharp map for a blurred
// canvas texture (baked once per blur/image change), or restore the sharp one
// when blur is 0. Cheap per-frame after the first bake.

export function syncImageRealShadows(
    host: ImageEffectHost,layerMesh: TODOAny, content: ImageContent) {
  if (!layerMesh) return
  const mat = layerMesh.material as THREE.MeshBasicMaterial
  const tex = (mat && mat.map) || null

  // --- cast ---
  const wantCast = !!content.castShadowReal
  if (layerMesh.castShadow !== wantCast) layerMesh.castShadow = wantCast
  // the mask is applied as an alphaMap on the visible material — feed it to the
  // shadow (depth/distance) materials too, so a masked image casts its mask
  // shape instead of the full rectangle (an opaque JPG has no map alpha)
  const alphaMap = (mat && (mat as TODOAny).alphaMap) || null
  if (wantCast) {
    if (
      !layerMesh.customDepthMaterial ||
      layerMesh.userData.realDepthTex !== tex ||
      layerMesh.userData.realDepthAlpha !== alphaMap
    ) {
      if (layerMesh.customDepthMaterial) layerMesh.customDepthMaterial.dispose()
      if (layerMesh.customDistanceMaterial)
        layerMesh.customDistanceMaterial.dispose()
      // depth material → used by spot/directional shadows
      layerMesh.customDepthMaterial = new THREE.MeshDepthMaterial({
        depthPacking: THREE.RGBADepthPacking,
        map: tex,
        ...(alphaMap ? { alphaMap } : {}),
        alphaTest: 0.5,
      })
      // distance material → used by POINT-light shadows (three picks this one
      // for point lights, so the cutout silhouette works there too)
      layerMesh.customDistanceMaterial = new THREE.MeshDistanceMaterial({
        map: tex,
        ...(alphaMap ? { alphaMap } : {}),
        alphaTest: 0.5,
      })
      layerMesh.userData.realDepthTex = tex
      layerMesh.userData.realDepthAlpha = alphaMap
    }
    // a background image plane is frustum-culled by default; make sure it's
    // always considered for the shadow pass so it actually casts
    layerMesh.frustumCulled = false
    // a flat plane only casts a shadow from the side three renders in the
    // shadow pass (back faces for a FrontSide material). Force DoubleSide for
    // the shadow pass so the image casts regardless of which way it faces the
    // light — without the user needing to enable the visible "Double Side".
    if (mat) mat.shadowSide = THREE.DoubleSide
  } else if (layerMesh.customDepthMaterial) {
    layerMesh.customDepthMaterial.dispose()
    layerMesh.customDepthMaterial = undefined
    if (layerMesh.customDistanceMaterial) {
      layerMesh.customDistanceMaterial.dispose()
      layerMesh.customDistanceMaterial = undefined
    }
    layerMesh.userData.realDepthTex = null
    if (mat) mat.shadowSide = null
  }

  // --- receive ---
  const wantReceive = !!content.receiveShadowReal
  let overlay = layerMesh.userData.realShadowReceiver as TODOAny
  if (wantReceive) {
    // (re)build if missing, the image geometry was swapped (new mesh / quality),
    // or the mask changed (the overlay clips the received shadow to the mask)
    if (
      !overlay ||
      overlay.geometry !== layerMesh.geometry ||
      overlay.userData.maskTex !== alphaMap
    ) {
      if (overlay) {
        layerMesh.remove(overlay)
        overlay.material.dispose()
      }
      const receiveMat = new THREE.ShadowMaterial({
        transparent: true,
        opacity: 0.45,
        side: THREE.DoubleSide,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
      })
      // ShadowMaterial has no map/alphaMap support — inject both, so the received
      // shadow only darkens where the image is actually visible: inside the mask
      // shape and on the opaque part of the file. Ohne die Bild-Alpha lag der
      // Schatten auch auf den durchsichtigen Raendern eines PNG und endete hart an
      // der Kante der Bildflaeche (Jans Fund 02.11.2026: „abgeschnittener Schatten“).
      // uv is declared by three's vertex prefix in r139.
      const bildUniform = { value: tex }
      receiveMat.onBeforeCompile = (shader) => {
        shader.uniforms.uBild = bildUniform
        if (alphaMap) shader.uniforms.uMask = { value: alphaMap }
        shader.vertexShader = shader.vertexShader
          .replace(
            '#include <common>',
            '#include <common>\nvarying vec2 vMaskUv;'
          )
          .replace('void main() {', 'void main() {\n\tvMaskUv = uv;')
        shader.fragmentShader = shader.fragmentShader
          .replace(
            'uniform float opacity;',
            `uniform float opacity;\nuniform sampler2D uBild;\n${
              alphaMap ? 'uniform sampler2D uMask;\n' : ''
            }varying vec2 vMaskUv;`
          )
          .replace(
            'gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );',
            `gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) * texture2D( uBild, vMaskUv ).a${
              alphaMap ? ' * texture2D( uMask, vMaskUv ).g' : ''
            } );`
          )
      }
      receiveMat.customProgramCacheKey = () =>
        alphaMap ? 'maskedShadowReceiver' : 'imageShadowReceiver'
      // let the global shadow-strength slider also lighten image-plane shadows
      // (ShadowMaterial's getShadowMask() calls the same patched getShadow())
      injectShadowLift(host, receiveMat)
      overlay = new THREE.Mesh(layerMesh.geometry, receiveMat)
      overlay.userData.maskTex = alphaMap
      overlay.userData.bildUniform = bildUniform
      overlay.name = '__realShadowReceiver'
      overlay.receiveShadow = true
      overlay.castShadow = false
      // sit a hair in front of the image (toward its camera-facing side) so it
      // never ties on depth with the image plane — kills the z-fight flicker
      // when moving. Scaled to the image size so it's invisible but decisive.
      const w = layerMesh.userData.width || 1000
      const h = layerMesh.userData.height || 1000
      overlay.position.z = Math.max(w, h) * 0.0015
      overlay.renderOrder = (layerMesh.renderOrder || 0) + 0.5
      layerMesh.add(overlay)
      layerMesh.userData.realShadowReceiver = overlay
    }
    // Die Bild-Textur wechselt ohne Neubau mit (Laden, Weichzeichnen); ohne
    // Textur ist nichts zu sehen, das einen Schatten tragen koennte.
    overlay.userData.bildUniform.value = tex
    overlay.visible = !!tex
    overlay.renderOrder = (layerMesh.renderOrder || 0) + 0.5
  } else if (overlay) {
    overlay.visible = false
  }
}